"use client";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import {
  X,
  Send,
  MessageCircle,
  Phone,
  Paperclip,
  Mail,
  PhoneCall,
  Smile,
  MessageSquare,
  FileText,
  Download,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import GetVisitorDetails from "../../components/open-chat/components/GetVisitorDetails";
import EmojiPicker from "emoji-picker-react";
import { useDispatch, useSelector } from "react-redux";
import {
  getOpenChatListAction,
  sendMessageOpenAction,
  uploadImageAction,
} from "../../services/action/common";
import socket from "../../components/socket/socket";
import { truncateString } from "../../components/utils/functions/functions";
import { Tooltip } from "@mui/material";
// import PhoneInput from "react-phone-input-2"
// import "react-phone-input-2/lib/style.css"

const initialMessages = [
  {
    id: 1,
    text: "Welcome to live Support. How can we help you today?",
    sender: "support",
    timestamp: "10:00 AM",
    type: "text",
  },
];

// Add the new theme for bestassignmenthelp-ca to the themes object
const themes = {
  default: {
    primary: "from-[#8B2F97] to-[#B455C1]",
    secondary: "text-[#8B2F97] hover:text-[#B455C1]",
    accent: "bg-purple-50",
  },
  green: {
    primary: "from-[#2F8B4F] to-[#55C17E]",
    secondary: "text-[#2F8B4F] hover:text-[#55C17E]",
    accent: "bg-green-50",
  },
  blue: {
    primary: "from-[#2F4F8B] to-[#5580C1]",
    secondary: "text-[#2F4F8B] hover:text-[#5580C1]",
    accent: "bg-blue-50",
  },
  red: {
    primary: "from-[#8B2F2F] to-[#C15555]",
    secondary: "text-[#8B2F2F] hover:text-[#C15555]",
    accent: "bg-red-50",
  },
  orange: {
    primary: "from-[#8B5F2F] to-[#C18955]",
    secondary: "text-[#8B5F2F] hover:text-[#C18955]",
    accent: "bg-orange-50",
  },
  teal: {
    primary: "from-[#2F8B8B] to-[#55C1C1]",
    secondary: "text-[#2F8B8B] hover:text-[#55C1C1]",
    accent: "bg-teal-50",
  },
  pink: {
    primary: "from-[#8B2F5F] to-[#C15589]",
    secondary: "text-[#8B2F5F] hover:text-[#C15589]",
    accent: "bg-pink-50",
  },
  "homeworkhelp-io": {
    primary: "from-[#F47B20] to-[#FF8C3A]",
    secondary: "text-[#F47B20] hover:text-[#FF8C3A]",
    accent: "bg-orange-50",
  },
  "247assignmentcare-com": {
    primary: "from-[#F47B20] to-[#FF5B8D]",
    secondary: "text-[#F47B20] hover:text-[#FF5B8D]",
    accent: "bg-orange-50",
  },
  "247myassignmenthelp-com": {
    primary: "from-[#F47B20] to-[#FF5B8D]",
    secondary: "text-[#F47B20] hover:text-[#FF5B8D]",
    accent: "bg-orange-50",
  },
  "casestudyhelp-uk": {
    primary: "from-[#1A1F36] to-[#2A3050]",
    secondary: "text-[#4A90E2] hover:text-[#6AACFF]",
    accent: "bg-blue-50",
  },
  "assignmentexpert-io": {
    primary: "from-[#0A6E31] to-[#0F8C3E]",
    secondary: "text-[#0A6E31] hover:text-[#0F8C3E]",
    accent: "bg-green-50",
  },
  "myobassignmenthelp-com": {
    primary: "from-[#2D5242] to-[#3A6A55]",
    secondary: "text-[#2D5242] hover:text-[#3A6A55]",
    accent: "bg-green-50",
  },
  "xeroassignmenthelp-com": {
    primary: "from-[#0F0F0F] to-[#1A1A1A]",
    secondary: "text-[#C6FF00] hover:text-[#D4FF33]",
    accent: "bg-gray-900",
  },
  "marketingassignmenthelp-io": {
    primary: "from-[#3B5998] to-[#5B7EC2]",
    secondary: "text-[#3B5998] hover:text-[#5B7EC2]",
    accent: "bg-blue-50",
  },
  "onlineexamhelp-io": {
    primary: "from-[#2D0A31] to-[#3D0B41]",
    secondary: "text-[#FFB800] hover:text-[#FFC833]",
    accent: "bg-purple-50",
  },
  "codingassignmenthelp-io": {
    primary: "from-[#7C4DFF] to-[#9E7BFF]",
    secondary: "text-[#7C4DFF] hover:text-[#9E7BFF]",
    accent: "bg-purple-50",
  },
  "engineeringassignmenthelp-com": {
    primary: "from-[#1E3A5F] to-[#2C4F7C]",
    secondary: "text-[#FFD700] hover:text-[#FFC107]",
    accent: "bg-slate-100",
  },
  "dissertationwritingservices-io": {
    primary: "from-[#4A148C] to-[#6A1B9A]",
    secondary: "text-[#4A148C] hover:text-[#6A1B9A]",
    accent: "bg-purple-50",
  },
  "courseworkhelp-io": {
    primary: "from-[#00695C] to-[#00897B]",
    secondary: "text-[#00695C] hover:text-[#00897B]",
    accent: "bg-teal-50",
  },
  "cookeryassignmenthelp-io": {
    primary: "from-[#1ABC9C] to-[#16A085]",
    secondary: "text-[#464E8A] hover:text-[#5C6BC0]",
    accent: "bg-emerald-50",
  },
  "statisticsassignmenthelp-io": {
    primary: "from-[#9BC17D] to-[#B4D495]",
    secondary: "text-[#D4813B] hover:text-[#E69656]",
    accent: "bg-green-50",
  },
  "onlinetafeassignmenthelp-com": {
    primary: "from-[#00838F] to-[#0097A7]",
    secondary: "text-[#00838F] hover:text-[#0097A7]",
    accent: "bg-cyan-50",
  },
  "scienceassignmenthelp-com": {
    primary: "from-[#00ACC1] to-[#00BCD4]",
    secondary: "text-[#00ACC1] hover:text-[#00BCD4]",
    accent: "bg-cyan-50",
  },
  "assessmenthelp-uk": {
    primary: "from-[#303F9F] to-[#3949AB]",
    secondary: "text-[#303F9F] hover:text-[#3949AB]",
    accent: "bg-indigo-50",
  },
  "programmingassignmenthelp-uk": {
    primary: "from-[#FF8B7B] to-[#FFA494]",
    secondary: "text-[#FF8B7B] hover:text-[#FF9B8B]",
    accent: "bg-rose-50",
  },
  "courseworkhelp-uk": {
    primary: "from-[#26B99A] to-[#1FA588]",
    secondary: "text-[#2E2B5F] hover:text-[#26B99A]",
    accent: "bg-emerald-50",
  },
  "examhelp-uk": {
    primary: "from-[#0066CC] to-[#0052A3]",
    secondary: "text-[#FFD700] hover:text-[#FFE44D]",
    accent: "bg-blue-50",
  },
  "assignmenthelpuk-io": {
    primary: "from-[#26B99A] to-[#1FA588]",
    secondary: "text-[#26B99A] hover:text-[#1FA588]",
    accent: "bg-emerald-50",
  },
  "engineeringassignmenthelp-uk": {
    primary: "from-[#6366F1] to-[#4F46E5]",
    secondary: "text-[#FF6B00] hover:text-[#FF8533]",
    accent: "bg-indigo-50",
  },
  "mbaassignmenthelp-uk": {
    primary: "from-[#FFC107] to-[#FFB300]",
    secondary: "text-[#FF4081] hover:text-[#FF80AB]",
    accent: "bg-amber-50",
  },
  "accountingassignmenthelp-uk": {
    primary: "from-[#00BFA5] to-[#00897B]",
    secondary: "text-[#1976D2] hover:text-[#2196F3]",
    accent: "bg-teal-50",
  },
  "managementassignmenthelp-uk": {
    primary: "from-[#1A237E] to-[#283593]",
    secondary: "text-[#1A237E] hover:text-[#283593]",
    accent: "bg-indigo-50",
  },
  "marketingassignmenthelp-uk": {
    primary: "from-[#9B4C9F] to-[#B66BB9]",
    secondary: "text-[#9B4C9F] hover:text-[#B66BB9]",
    accent: "bg-purple-50",
  },
  "academicassignmenthelp-com": {
    primary: "from-[#FFD700] to-[#F5C400]",
    secondary: "text-[#FFD700] hover:text-[#F5C400]",
    accent: "bg-gray-900",
  },
  "bestassignmenthelp-ca": {
    primary: "from-[#00CED1] to-[#00B4B9]",
    secondary: "text-[#00CED1] hover:text-[#00B4B9]",
    accent: "bg-cyan-50",
  },
  "bestassignmenthelp-de": {
    primary: "from-[#563D52] to-[#6A4A64]",
    secondary: "text-[#563D52] hover:text-[#6A4A64]",
    accent: "bg-purple-50",
  },
  "bestonlineassignmenthelp-co-uk": {
    primary: "from-[#9B6B9B] to-[#C8A2C8]",
    secondary: "text-[#9B6B9B] hover:text-[#C8A2C8]",
    accent: "bg-purple-50",
  },
  "bestsophelp-com": {
    primary: "from-[#0066CC] to-[#0052A3]",
    secondary: "text-[#0066CC] hover:text-[#0052A3]",
    accent: "bg-blue-50",
  },
  "domyassignmentsforme-uk": {
    primary: "from-[#1DB9A0] to-[#19A08C]",
    secondary: "text-[#1DB9A0] hover:text-[#19A08C]",
    accent: "bg-teal-50",
  },
  "myassignment-uk": {
    primary: "from-[#3498DB] to-[#2980B9]",
    secondary: "text-[#3498DB] hover:text-[#2980B9]",
    accent: "bg-blue-50",
  },
  "nursingassignments-uk": {
    primary: "from-[#00BFA5] to-[#00A693]",
    secondary: "text-[#00BFA5] hover:text-[#00A693]",
    accent: "bg-teal-50",
  },
  "payforassignment-uk": {
    primary: "from-[#4169E1] to-[#3457C0]",
    secondary: "text-[#4169E1] hover:text-[#3457C0]",
    accent: "bg-blue-50",
  },
  "researchproposalwriter-com": {
    primary: "from-[#FF6B6B] to-[#FF5252]",
    secondary: "text-[#FF6B6B] hover:text-[#FF5252]",
    accent: "bg-red-50",
  },
  "dissertationwriter-uk": {
    primary: "from-[#FFD700] to-[#FFC400]",
    secondary: "text-[#FFD700] hover:text-[#FFC400]",
    accent: "bg-yellow-50",
  },
};

const AssignmentWriterAu = () => {
  const { openChatList } = useSelector((state) => state.commonReducer);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberValid, setPhoneNumberValid] = useState(false);
  const [phoneNumberSubmitted, setPhoneNumberSubmitted] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const chatContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const tooltipTimeoutRef = useRef();
  const audioRef = useRef(null);
  const inputRef = useRef(null);
  const [userId, setUserId] = useState("");

  const [chatAlign, setChatAlign] = useState("right");

  const dispatch = useDispatch();
  useEffect(() => {
    socket.on("exits_user", (existUser) => {
      setUserId(existUser?.userId);
    });
    return () => {
      socket.off("exits_user");
    };
  }, []);

  useEffect(() => {
    socket.on("new_open_chat_coming", (data) => {
      if (userId == data?.userId) {
        playNotificationSound();
        dispatch(getOpenChatListAction({ userId }));
        setIsOpen(true);
      }
    });
    return () => {
      socket.off("new_open_chat_coming");
    };
  }, [dispatch, userId]);

  // Format openChatList data to match the expected message structure
  const formattedMessages = useMemo(() => {
    if (!openChatList?.chatList || openChatList?.chatList?.length === 0)
      return initialMessages;
    return openChatList?.chatList?.map((message) => ({
      ...message,
      id: message._id,
      text: message.content,
      timestamp: new Date(message.timestamp),
      type: message.messageType || message.type || "text",
      attachments: message.attachments || [],
    }));
  }, [openChatList]);

  // Combine formatted messages from openChatList and local messages
  const allMessages = useMemo(() => {
    return [...formattedMessages];
  }, [formattedMessages]);

  useEffect(() => {
    if (userId) {
      dispatch(getOpenChatListAction({ userId }));
    }
  }, [dispatch, userId]);

  const [metaData, setMetaData] = useState({
    whatsappNo: "",
    phoneNumber: "",
    email: "",
    countryCode: "",
  });

  // Now add the case for bestassignmenthelp-ca in the theme selection logic
  const theme = useMemo(() => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      const whatsappNo = urlParams.get("whatsappNo");
      const phoneNumber = urlParams.get("phoneNumber");
      const email = urlParams.get("email");
      const countryCode = urlParams.get("countryCode");
      const chatAlignment = urlParams.get("chatAlignment");
      // Ensure chatAlign is explicitly set to "left" if that's the URL parameter
      setChatAlign(chatAlignment === "left" ? "left" : "right");
      setMetaData({
        whatsappNo,
        email,
        phoneNumber,
        countryCode,
      });
      const theme = urlParams.get("theme");
      switch (theme) {
        case "courseworkhelp-uk":
          return themes.green;
        case "assignmentwriter-au":
          return themes.default;
        case "homeworkhelp-io":
          return themes["homeworkhelp-io"];
        case "247assignmentcare-com":
        case "247myassignmenthelp-com":
          return themes["247assignmentcare-com"];
        case "casestudyhelp-uk":
          return themes["casestudyhelp-uk"];
        case "assignmentexpert-io":
          return themes["assignmentexpert-io"];
        case "myobassignmenthelp-com":
          return themes["myobassignmenthelp-com"];
        case "xeroassignmenthelp-com":
          return themes["xeroassignmenthelp-com"];
        case "marketingassignmenthelp-io":
          return themes["marketingassignmenthelp-io"];
        case "onlineexamhelp-io":
          return themes["onlineexamhelp-io"];
        case "codingassignmenthelp-io":
          return themes["codingassignmenthelp-io"];
        case "engineeringassignmenthelp-com":
          return themes["engineeringassignmenthelp-com"];
        case "dissertationwritingservices-io":
          return themes["dissertationwritingservices-io"];
        case "courseworkhelp-io":
          return themes["courseworkhelp-io"];
        case "cookeryassignmenthelp-io":
          return themes["cookeryassignmenthelp-io"];
        case "statisticsassignmenthelp-io":
          return themes["statisticsassignmenthelp-io"];
        case "onlinetafeassignmenthelp-com":
          return themes["onlinetafeassignmenthelp-com"];
        case "scienceassignmenthelp-com":
          return themes["scienceassignmenthelp-com"];
        case "assessmenthelp-uk":
          return themes["assessmenthelp-uk"];
        case "programmingassignmenthelp-uk":
          return themes["programmingassignmenthelp-uk"];
        case "courseworkhelp-uk":
          return themes["courseworkhelp-uk"];
        case "examhelp-uk":
          return themes["examhelp-uk"];
        case "assignmenthelpuk-io":
          return themes["assignmenthelpuk-io"];
        case "engineeringassignmenthelp-uk":
          return themes["engineeringassignmenthelp-uk"];
        case "mbaassignmenthelp-uk":
          return themes["mbaassignmenthelp-uk"];
        case "accountingassignmenthelp-uk":
          return themes["accountingassignmenthelp-uk"];
        case "managementassignmenthelp-uk":
          return themes["managementassignmenthelp-uk"];
        case "marketingassignmenthelp-uk":
          return themes["marketingassignmenthelp-uk"];
        case "academicassignmenthelp-com":
          return themes["academicassignmenthelp-com"];
        case "bestassignmenthelp-ca":
          return themes["bestassignmenthelp-ca"];
        case "bestassignmenthelp-de":
          return themes["bestassignmenthelp-de"];
        // Add more cases for other websites
        case "bestonlineassignmenthelp-co-uk":
          return themes["bestonlineassignmenthelp-co-uk"];
        case "bestsophelp-com":
          return themes["bestsophelp-com"];
        case "domyassignmentsforme-uk":
          return themes["domyassignmentsforme-uk"];
        case "myassignment-uk":
          return themes["myassignment-uk"];
        case "nursingassignments-uk":
          return themes["nursingassignments-uk"];
        case "payforassignment-uk":
          return themes["payforassignment-uk"];
        case "researchproposalwriter-com":
          return themes["researchproposalwriter-com"];
        case "dissertationwriter-uk":
          return themes["dissertationwriter-uk"];
        default:
          return themes.default;
      }
    }
    return themes.default;
  }, []);

  useEffect(() => {
    tooltipTimeoutRef.current = setTimeout(() => {
      setShowTooltip(true);
    }, 5000);

    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [allMessages]); // Added allMessages as dependency to scroll when new messages arrive

  useEffect(() => {
    // Function to handle clicking outside the emoji picker
    const handleClickOutside = (event) => {
      const emojiPickerContainer = document.querySelector(
        ".emoji-picker-container"
      );
      if (
        showEmojiPicker &&
        emojiPickerContainer &&
        !emojiPickerContainer.contains(event.target) &&
        !event.target
          .closest("button")
          ?.contains(document.querySelector('svg[data-icon="smile"]'))
      ) {
        setShowEmojiPicker(false);
      }
    };

    // Function to handle pressing the Escape key
    const handleEscKey = (event) => {
      if (showEmojiPicker && event.key === "Escape") {
        setShowEmojiPicker(false);
      }
    };

    // Add event listeners
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscKey);

    // Clean up event listeners
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [showEmojiPicker]);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };

  // Function to scroll to and highlight a specific message
  const scrollToMessage = (messageId) => {
    if (chatContainerRef.current && messageId) {
      const messageElement = document.getElementById(`message-${messageId}`);
      if (messageElement) {
        messageElement.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightedMessageId(messageId);

        setTimeout(() => {
          setHighlightedMessageId(null);
        }, 2000);
      }
    }
  };

  const playNotificationSound = () => {
    if (audioRef.current) {
      audioRef.current
        .play()
        .catch((error) => console.error("Error playing sound:", error));
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    setShowTooltip(false);
    if (!isOpen) {
      setShowNotification(false);
    }
  };

  // Update the handlePhoneChange function to format the phone number properly
  const handlePhoneChange = (value, data) => {
    setPhoneNumber(value);
    // Validate phone number - basic validation for demonstration
    // At least 8 digits after country code
    setPhoneNumberValid(value.length >= data.dialCode.length + 8);
  };

  // Update the handlePhoneSubmit function to ensure the phone number is formatted with country code
  const handlePhoneSubmit = () => {
    if (phoneNumberValid && !phoneNumberSubmitted) {
      // Get the phone number with proper formatting
      // The PhoneInput component already formats with country code, but we need to ensure the + prefix
      const formattedPhoneNumber = phoneNumber.startsWith("+")
        ? phoneNumber
        : `+${phoneNumber}`;

      // Create a message with the properly formatted phone number
      const newMessage = {
        content: formattedPhoneNumber, // This ensures the format is "+countryCode number"
        timestamp: new Date(),
        messageType: "text",
        type: "number",
        clientId: userId,
        sender: userId,
      };

      dispatch(sendMessageOpenAction({ data: newMessage }));
      setPhoneNumberSubmitted(true);

      // Add a confirmation message from support
      setTimeout(() => {
        const confirmationMessage = {
          id: Date.now(),
          text: "Thank you for providing your contact number. How can we assist you today?",
          sender: "support",
          timestamp: new Date(),
          type: "text",
        };
        setMessages((prev) => [...prev, confirmationMessage]);
      }, 500);
    }
  };

  // Check if a URL is an image
  const isImageUrl = useCallback((url) => {
    if (!url) return false;
    return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(url);
  }, []);

  const handleSendMessage = (text) => {
    if ((text.trim() || attachments.length > 0) && userId) {
      const newMessage = {
        content: text.trim(),
        timestamp: new Date(),
        messageType: attachments.length > 0 ? "file" : "text",
        type: attachments.length > 0 ? "file" : "text",
        clientId: userId,
        sender: userId,
        replyTo: replyingTo?._id,
        attachments: attachments,
      };

      dispatch(sendMessageOpenAction({ data: newMessage }));
      setInputMessage("");
      setReplyingTo(null);
      setAttachments([]);
    }
  };

  const handleFileUpload = useCallback(
    (e) => {
      if (e.target.files.length > 0) {
        setIsUploading(true);

        const imageCallBack = (res) => {
          setAttachments((prev) => [...prev, ...(res?.imageUrls || [])]);
          setIsUploading(false);
        };

        const formData = new FormData();
        for (let i = 0; i < e.target.files.length; i++) {
          formData.append("images", e.target.files[i]);
        }

        dispatch(uploadImageAction({ data: formData, cb: imageCallBack }));
        e.target.value = null;
      }
    },
    [dispatch]
  );

  const removeAttachment = useCallback((index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const toggleEmojiPicker = () => {
    setShowEmojiPicker((prev) => !prev);
  };

  const addEmoji = (emojiObject) => {
    setInputMessage((prevMessage) => prevMessage + emojiObject.emoji);
  };

  const handleReply = (message) => {
    setReplyingTo(message);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Function to handle clicking on a reply preview
  const handleReplyClick = (replyToId) => {
    // Find the original message that was replied to
    const originalMessage = allMessages.find(
      (msg) => msg._id === replyToId || msg.id === replyToId
    );
    if (originalMessage) {
      scrollToMessage(originalMessage.id || originalMessage._id);
    }
  };

  const renderMessageContent = useCallback(
    (message) => {
      // Check if the message has content or attachments
      if (
        !message.text &&
        !message.content &&
        (!message.attachments || message.attachments.length === 0)
      ) {
        return <p className="text-sm break-words text-gray-700">No content</p>;
      }

      // Handle messages with attachments array
      if (message.attachments && message.attachments.length > 0) {
        return (
          <div className="mt-1">
            {message.attachments.map((url, index) => {
              if (isImageUrl(url)) {
                return (
                  <div key={index} className="mb-2">
                    <img
                      src={url || "/placeholder.svg"}
                      alt={`Attachment ${index + 1}`}
                      className="rounded-md max-h-60 w-auto object-contain border border-gray-200"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/placeholder.svg";
                      }}
                    />
                  </div>
                );
              } else {
                // For non-image files
                return (
                  <div key={index} className="mt-1 flex items-center mb-2">
                    <div className="p-2 bg-gray-100 rounded-md mr-2">
                      <FileText className="w-6 h-6 text-gray-500" />
                    </div>
                    <div>
                      <div className="text-sm font-medium">Document</div>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-xs mt-1 inline-block px-2 py-0.5 rounded ${
                          message.sender?._id === userId
                            ? "bg-white text-purple-700"
                            : "bg-purple-100 text-purple-700"
                        }`}
                      >
                        <span className="flex items-center">
                          <Download className="w-3 h-3 mr-1" /> Download
                        </span>
                      </a>
                    </div>
                  </div>
                );
              }
            })}
            {(message.text || message.content) && (
              <p className="text-sm break-words mt-2">
                {message.text || message.content}
              </p>
            )}
          </div>
        );
      }

      // If no attachments, just show the message text
      return <p className="break-words">{message.text || message.content}</p>;
    },
    [isImageUrl, userId]
  );

  const handleInputChange = (e) => {
    setInputMessage(e.target.value);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const chatAlignment = urlParams.get("chatAlignment");
    setChatAlign(chatAlignment === "left" ? "left" : "right");
  }, []);

  useEffect(() => {
    // Create a MutationObserver to watch for changes
    const observer = new MutationObserver((mutations) => {
      const hideChat = document.querySelector(".hide-open-chat-are");

      const action = hideChat ? "hideChat" : "showChat";
      window.parent.postMessage({ action }, "*");
    });
    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    const initialHideChat = document.querySelector(".hide-open-chat-are");
    window.parent.postMessage(
      { action: initialHideChat ? "hideChat" : "showChat" },
      "*"
    );

    // Cleanup observer on unmount
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className={`${isOpen ? "" : "hide-open-chat-are"}`}>
      <GetVisitorDetails />
      <audio ref={audioRef} src="/assets/seen.wav" />
      <div
        className={`fixed bottom-4 ${
          chatAlign === "left" ? "left-4 sm:left-6" : "right-4 sm:right-6"
        } flex flex-col ${
          chatAlign === "left" ? "items-start" : "items-end"
        } gap-4 z-50`}
      >
        {/* {showTooltip && !isOpen && (
          <div className="transform transition-all duration-300 ease-out animate-fadeIn max-w-xs">
            <div className="relative bg-white rounded-lg shadow-lg p-3 mb-2">
              <div
                className={`absolute -bottom-2 ${chatAlign === "left" ? "left-6" : "right-6"} w-4 h-4 bg-white transform rotate-45`}
              ></div>
              <div className="flex items-center gap-3 text-sm">
                <div
                  className={`flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-r ${theme.primary} flex items-center justify-center text-white`}
                >
                  <MessageCircle className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Need Help?</p>
                  <div className="mt-1 space-y-1">
                    <a
                      href={`mailto:${metaData?.email}`}
                      className={`flex items-center gap-1.5 text-xs ${theme.secondary} transition-colors`}
                    >
                      <Mail className="w-3.5 h-3.5" />
                      {metaData?.email}
                    </a>
                    <a
                      href={`tel:+${metaData.countryCode + metaData.phoneNumber}`}
                      className={`flex items-center gap-1.5 text-xs ${theme.secondary} transition-colors`}
                    >
                      <PhoneCall className="w-3.5 h-3.5" />+{metaData?.countryCode} {metaData?.phoneNumber}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )} */}

        <button
          onClick={toggleChat}
          className={`relative bg-gradient-to-r ${
            theme.primary
          } text-white rounded-full pl-3 pr-4 sm:pl-4 sm:pr-6 py-3 flex items-center gap-2 sm:gap-3 shadow-lg hover:shadow-xl transition-all duration-300 group ${
            isOpen ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
        >
          <div className="relative">
            <img
              src="/avatar.jpeg"
              alt="Support Agent"
              className="w-8 h-8 rounded-full border-2 border-white"
            />
            <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
          </div>
          <div className="flex flex-col items-start">
            <span className="text-xs font-medium opacity-90">Live Chat</span>
            <span className="font-medium text-sm">Need Help?</span>
          </div>
          {showNotification && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-ping">
              1
            </span>
          )}
        </button>
      </div>

      <div
        className={`fixed bottom-0 ${
          chatAlign === "left" ? "left-0" : "right-0"
        } w-[89%] sm:w-96 min-h-[calc(100vh-2rem)] max-h-[calc(100vh-2rem)] bg-gradient-to-b ${
          theme.accent
        } to-[#E6F7FF] sm:rounded-lg shadow-2xl transition-all duration-500 transform ${
          isOpen
            ? "scale-100 opacity-100 translate-y-0"
            : "scale-95 opacity-0 translate-y-12 pointer-events-none hide-open-chat-are"
        } z-50 overflow-hidden flex flex-col`}
      >
        <div
          className={`flex justify-between items-center px-4 py-3 bg-gradient-to-r ${theme.primary} text-white`}
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <img
                src="/avatar.jpeg"
                alt="Support Agent"
                className="w-8 h-8 rounded-full border-2 border-white"
              />
              <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white"></div>
            </div>
            <div>
              <h2 className="text-base font-medium text-white">
                Support Clickinpedia
              </h2>
              <p className="text-xs text-white/80">Online | How can we help?</p>
            </div>
          </div>
          <button
            onClick={toggleChat}
            className="text-white/80 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div
          ref={chatContainerRef}
          className="flex-grow overflow-y-auto p-4 space-y-3 bg-gray-50/50"
        >
          {allMessages.map((message) => (
            <div
              key={message.id || message._id}
              id={`message-${message.id || message._id}`}
              className={`mb-4 transition-all duration-300 ${
                message.id === highlightedMessageId ||
                message._id === highlightedMessageId
                  ? "bg-yellow-100 rounded-lg p-2 -mx-2"
                  : ""
              }`}
            >
              <div
                className={`flex ${
                  message?.sender?._id === userId
                    ? "justify-end"
                    : "justify-start"
                } animate-fadeIn items-start gap-2 mb-2`}
              >
                {message.sender === "support" && (
                  <div className="relative flex-shrink-0">
                    <img
                      src="/avatar.jpeg"
                      alt="Support Agent"
                      className="w-8 h-8 rounded-full"
                    />
                    <div className="absolute -bottom-1 -right-1 w-2 h-2 bg-green-500 rounded-full border-2 border-white"></div>
                  </div>
                )}
                <div className="relative max-w-[85%] group">
                  <div
                    className={`w-full rounded-lg ${
                      message.replyTo ? "rounded-tl-none" : ""
                    } p-3 ${
                      message?.sender?._id === userId
                        ? `bg-gradient-to-r ${theme.primary} text-white`
                        : "bg-white text-gray-800"
                    } shadow-sm`}
                  >
                    {message.replyTo && (
                      <div
                        className="mb-2 p-2 bg-gray-100 rounded-lg text-sm cursor-pointer hover:bg-gray-200 transition-colors"
                        onClick={() => handleReplyClick(message.replyTo._id)}
                      >
                        <Tooltip
                          title={
                            message.replyTo?.content || message.replyTo?.text
                          }
                        >
                          <p className="font-semibold text-black">
                            {truncateString(
                              message.replyTo?.content || message.replyTo?.text,
                              20
                            )}
                          </p>
                        </Tooltip>
                      </div>
                    )}
                    {renderMessageContent(message)}
                  </div>
                  <button
                    onClick={() => handleReply(message)}
                    className={`absolute ${
                      message?.sender?._id === userId ? "-left-8" : "-right-8"
                    } top-2 p-1.5 rounded-full ${
                      message?.sender?._id === userId
                        ? `bg-gradient-to-r ${theme.primary} text-white`
                        : `${theme.accent} ${theme.secondary}`
                    } transition-all duration-200 opacity-50 hover:opacity-100`}
                  >
                    <MessageSquare size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="p-3 bg-white border-t border-gray-100">
          {replyingTo && (
            <div className="flex items-center justify-between bg-gray-100 p-2 rounded-lg mb-2">
              <div className="flex-1 truncate">
                <span className="font-semibold">
                  {replyingTo.sender === "user" ? "You" : "Support"}:
                </span>{" "}
                {replyingTo.text || replyingTo.content}
              </div>
              <button
                onClick={() => setReplyingTo(null)}
                className="ml-2 text-gray-500 hover:text-gray-700"
              >
                <X size={16} />
              </button>
            </div>
          )}

          {/* {!phoneNumberSubmitted && !openChatList?.chatList && (
          <div className="mb-3">
            <div className="flex flex-col">
              <div className="relative">
                <PhoneInput
                  country={"au"}
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  inputStyle={{
                    width: "100%",
                    height: "48px",
                    fontSize: "1rem",
                    borderRadius: "0.75rem",
                    border: "2px solid rgba(139, 47, 151, 0.4)",
                    paddingLeft: "57px", // Increased padding to accommodate country code
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                    transition: "all 0.3s ease",
                  }}
                  buttonStyle={{
                    borderTopLeftRadius: "0.75rem",
                    borderBottomLeftRadius: "0.75rem",
                    borderRight: "2px solid rgba(139, 47, 151, 0.2)",
                    backgroundColor: "rgba(139, 47, 151, 0.05)",
                    padding: "0 12px 0 10px", // Added padding to the right of the flag
                  }}
                  containerStyle={{
                    width: "100%",
                    marginBottom: "0.5rem",
                  }}
                  dropdownStyle={{
                    borderRadius: "0.75rem",
                    border: "2px solid rgba(139, 47, 151, 0.3)",
                    boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)",
                    width: "300px",
                    maxHeight: "300px",
                    overflow: "auto",
                    zIndex: 999,
                    position: "absolute",
                    bottom: "100%",
                    marginBottom: "8px",
                  }}
                  searchStyle={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "0.5rem",
                    border: "1px solid rgba(139, 47, 151, 0.3)",
                    margin: "8px 0",
                    fontSize: "0.9rem",
                  }}
                  enableSearch={true}
                  disableSearchIcon={false}
                  searchPlaceholder="Search country..."
                  countryCodeEditable={false}
                  preferredCountries={["au", "us", "gb", "ca", "nz"]}
                  inputProps={{
                    placeholder: "Enter your mobile number",
                  }}
                  specialLabel={""}
                  dropdownClass="phone-dropdown-above"
                  autoFormat={true} // Enable auto-formatting
                  formatOnInit={true} // Format on initialization
                  prefix="+" // Ensure the + prefix is always displayed
                />
              </div>
              <div className="flex items-center mt-2">
                <div
                  className={`h-1.5 flex-grow rounded-full transition-all duration-500 ${
                    phoneNumberValid ? "bg-green-500" : "bg-gray-300"
                  }`}
                  style={{
                    background: phoneNumberValid
                      ? "linear-gradient(to right, #8B2F97, #B455C1)"
                      : "linear-gradient(to right, #e5e7eb, #d1d5db)",
                  }}
                ></div>
              </div>
              <div className="flex justify-between items-center mt-3">
                <p className="text-xs text-gray-500">
                  {phoneNumberValid ? (
                    <span className="text-green-600 flex items-center font-medium">
                      <Check size={14} className="mr-1" /> Valid number
                    </span>
                  ) : (
                    "Please enter a valid mobile number"
                  )}
                </p>
                <button
                  onClick={handlePhoneSubmit}
                  disabled={!phoneNumberValid}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium ${
                    phoneNumberValid
                      ? `bg-gradient-to-r ${theme.primary} text-white hover:opacity-90`
                      : "bg-gray-200 text-gray-500 cursor-not-allowed"
                  } transition-all duration-300 shadow-md hover:shadow-lg`}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        )} */}

          {/* Attachments display */}
          {attachments.length > 0 && (
            <div className="p-2 bg-gray-50 rounded-lg mb-3">
              <div className="text-xs font-medium text-gray-500 mb-2">
                Attachments:
              </div>
              <div className="flex flex-wrap gap-2">
                {attachments.map((url, index) => {
                  const fileName = url.split("/").pop();
                  const isImage = isImageUrl(url);

                  return (
                    <div key={index} className="relative group">
                      {isImage ? (
                        <div className="w-20 h-20 border border-gray-200 rounded-md overflow-hidden bg-white">
                          <img
                            src={url || "/placeholder.svg"}
                            alt={fileName}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/placeholder.svg";
                            }}
                          />
                        </div>
                      ) : (
                        <div className="flex items-center bg-white rounded-md p-1.5 border border-gray-200">
                          <FileText className="w-4 h-4 text-gray-500 mr-1.5" />
                          <span className="text-xs text-gray-700 mr-1.5 max-w-[150px] truncate">
                            {fileName}
                          </span>
                        </div>
                      )}
                      <button
                        onClick={() => removeAttachment(index)}
                        className="absolute -top-1 -right-1 p-0.5 rounded-full bg-red-500 text-white hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Loading indicator for file uploads */}
          {isUploading && (
            <div className="p-2 bg-gray-50 rounded-lg mb-3">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-500 mr-2"></div>
                <span className="text-xs text-gray-600">Uploading file...</span>
              </div>
            </div>
          )}

          {/* Only show message input and action buttons if phone number is submitted */}
          {/* {(phoneNumberSubmitted || openChatList?.chatList?.length > 0) && ( */}
          <>
            <div className="flex justify-around mb-3 px-2">
              <button
                onClick={toggleEmojiPicker}
                className={`${theme.secondary} transition-colors p-2 rounded-full hover:${theme.accent}`}
              >
                <Smile size={18} />
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className={`${theme.secondary} transition-colors p-2 rounded-full hover:${theme.accent}`}
              >
                <Paperclip size={18} />
              </button>
              <a
                href={`https://wa.me/${
                  metaData?.countryCode + metaData?.whatsappNo
                }`}
                target="_blank"
                rel="noopener noreferrer"
                className={`${theme.secondary} hover:text-[#25D366] transition-colors p-2 rounded-full hover:${theme.accent}`}
              >
                <FaWhatsapp size={18} />
              </a>
              <a
                href={`tel:+${metaData.countryCode + metaData?.phoneNumber}`}
                className={`${theme.secondary} transition-colors p-2 rounded-full hover:${theme.accent}`}
              >
                <Phone size={18} />
              </a>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage(inputMessage);
              }}
              className="flex items-center gap-2 px-2"
            >
              <div className="flex items-center gap-2 px-2 w-full">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputMessage}
                  onChange={handleInputChange}
                  placeholder={
                    replyingTo ? "Type your reply..." : "Type your message..."
                  }
                  className="flex-grow px-4 py-2.5 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-1 focus:ring-purple-400"
                />
                <button
                  type="submit"
                  className={`bg-gradient-to-r ${theme.primary} text-white p-2.5 rounded-full hover:opacity-90 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-purple-400`}
                  disabled={!inputMessage.trim() && attachments.length === 0}
                >
                  <Send size={18} />
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileUpload}
                style={{ display: "none" }}
              />
            </form>
          </>
          {/* )} */}
        </div>
        {showEmojiPicker && (
          <div
            className={`absolute bottom-20 ${
              chatAlign === "left" ? "left-4" : "right-4"
            } z-50 emoji-picker-container`}
          >
            <div className="relative">
              <button
                onClick={() => setShowEmojiPicker(false)}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              >
                <X size={18} />
              </button>
              <EmojiPicker onEmojiClick={addEmoji} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignmentWriterAu;
